'use client';
import React, { Component } from 'react';
import styled from 'styled-components';

import { reportCriticalError, Telemetry } from '../hooks/useTelemetry';
import localstorageService from '../LocalstorageService';
import {
  claimChunkLoadAutoReload,
  CriticalApplicationError,
  isChunkLoadError,
  isLikelyBrowserExtensionCrash,
} from '@/typings/errors';
import {
  consumeAutoReloadPending,
  CRASH_CONFIRM_MS,
  markAutoReloadPending,
} from '@/utils/crashReport';
import { clearAllCookies } from '@/utils/cookies';
import { reloadPage } from '@/utils/reloadPage';

interface ErrorState {
  hasError: boolean;
  isAutoReloading: boolean;
}
interface ErrorProps {
  children: React.ReactNode;
}

class ErrorBoundary extends Component<ErrorProps, ErrorState> {
  private crashReportTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(props: ErrorProps) {
    super(props);
    this.state = { hasError: false, isAutoReloading: false };
  }

  componentWillUnmount() {
    // Navigation (auto-reload, or the student leaving the crash screen)
    // drops the pending report. The exception is what pages oncall, so a
    // document that didn't stay on the crash UI never sends it.
    this.clearCrashReport();
  }

  private clearCrashReport() {
    if (this.crashReportTimer !== undefined) {
      clearTimeout(this.crashReportTimer);
      this.crashReportTimer = undefined;
    }
  }

  private scheduleCrashReport(
    error: CriticalApplicationError,
    context: Record<string, string>,
  ) {
    this.clearCrashReport();
    this.crashReportTimer = setTimeout(() => {
      this.crashReportTimer = undefined;
      reportCriticalError(error, context);
    }, CRASH_CONFIRM_MS);
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // A failed lazy chunk is almost always a transient network drop — the
    // chunk is still on the CDN. One reload recovers the student without
    // them ever seeing the crash screen. Deliberately NOT the "Try again"
    // path: that also wipes localStorage/cookies, which would drop their
    // session and drafts.
    const isAutoReloading =
      isChunkLoadError(error) && claimChunkLoadAutoReload();

    this.setState({
      hasError: true,
      isAutoReloading,
    });

    // Create a CriticalApplicationError with the caught error as inner error
    const criticalError = new CriticalApplicationError(
      `Application render error: ${error.message}`,
      'client',
      error,
    );

    if (isAutoReloading) {
      // Not a crash yet: the student sees "Reconnecting…" and the reload
      // either recovers them or it doesn't. The oncall alert counts
      // CriticalApplicationError exceptions with no grace period, so this
      // path must not emit one. The next document calls
      // noteAutoReloadRecovered() once the lesson chunk loads
      // (`app_auto_reload_recovered`). If the chunk fails again inside the
      // cooldown, that document shows the crash UI and, only if it is still
      // mounted after CRASH_CONFIRM_MS, reports below with
      // autoReloadFailed. A plain event, so unlike reportCriticalError it's
      // subject to session sampling and needs Faro already loaded.
      markAutoReloadPending();
      if (process.env.REACT_APP_ENV !== 'development') {
        Telemetry()?.addPageAction('chunk_load_auto_reloaded', {
          errorMessage: error.message,
        });
      }
      this.clearCrashReport();
      reloadPage();
    } else if (process.env.REACT_APP_ENV !== 'development') {
      // The crash UI is on screen. Wait until it has stayed there before
      // reporting — an unmount (the auto-reload above, or the student
      // leaving) cancels the timer, and the alert never fires. The inner
      // (original) error rides in `context`, not just concatenated into
      // criticalError.stack — Faro's stack-trace parser only extracts
      // `at ...` frame lines and silently drops everything else, so a
      // string-concatenated "Inner Error:" section gets flattened together
      // with the wrapper's own frames with nothing indicating where one
      // stack ends and the other begins. `context` isn't touched by that
      // parser, so it survives as its own clearly-labeled field. React's
      // componentStack — the component tree path to where the render
      // actually failed — is included too; it was previously only ever
      // console.error'd, never reported anywhere.
      //
      // `reportCriticalError` rather than `noticeError`: a crash must reach
      // Grafana even from a sampled-out session, after the rate budget is
      // spent, or before Faro has finished loading.
      const autoReloadFailed = consumeAutoReloadPending();
      this.scheduleCrashReport(criticalError, {
        innerErrorMessage: error.message,
        innerErrorStack: error.stack ?? 'no stack available',
        componentStack: errorInfo.componentStack ?? 'no component stack',
        // Queryable in Grafana instead of pattern-matching message text by
        // hand every time — see isLikelyBrowserExtensionCrash's own comment
        // for what this can and can't catch.
        likelyBrowserExtensionCrash: String(
          isLikelyBrowserExtensionCrash(error),
        ),
        // Present on every confirmed crash so a log line can be told apart
        // from a build that reported at the moment of the throw.
        confirmedCrash: 'true',
        confirmAfterMs: String(CRASH_CONFIRM_MS),
        ...(autoReloadFailed ? { autoReloadFailed: 'true' } : {}),
      });
    }

    // Log additional context for debugging
    // eslint-disable-next-line no-console
    console.error('Critical application error:', criticalError);
    // eslint-disable-next-line no-console
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.isAutoReloading) {
      return <Container>Reconnecting…</Container>;
    }
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Container>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
              backgroundColor: '#F9F3EB',
            }}>
            <h2 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              Oops, there is an error!
            </h2>
            <h3 style={{ width: '100%', textAlign: 'center', margin: '20px' }}>
              We have informed our developers, try again.
            </h3>
            <span
              style={{
                width: '100%',
                textAlign: 'center',
                margin: '0 20px 20px',
              }}>
              Browser extensions (like ad blockers or writing assistants) are a
              common cause — try disabling them, then try again.
            </span>
            <span style={{ width: '100%', textAlign: 'center' }}>
              If this persists, please have your teacher contact support
            </span>

            <button
              onClick={() => {
                window.location.reload();

                //Clear all local storage to start completely fresh
                localstorageService.clear();
                try {
                  clearAllCookies();
                } catch (e) {}
              }}
              style={{
                margin: '20px',
                border: 'none',
                backgroundColor: '#33A0DD',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '24px',
                padding: '20px',
                color: 'white',
              }}>
              Try again
            </button>
          </div>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

const StyledButton = styled.button`
  padding: 20px;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  flex-direction: column;
`;
